<template>
  <div
    class="absolute top-0 left-0 h-full w-full object-cover"
    :class="cssClass"
  >
    <video-js
      ref="playerRef"
      class="video-js absolute top-0 left-0 h-full w-full object-cover"
    />
    <Picture
      v-if="poster"
      v-bind="poster"
      lazy
      class="absolute top-0 left-0 h-full w-full"
      img-class="block h-full w-full object-cover"
    />
    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <button
        v-if="showBtnPlay"
        class="w-16 h-16 flex  items-center justify-center rounded-full shadow focus:outline-none focus:bg-blue-grey"
        :class="buttonClass"
        @click="onHandleClick"
      >
        <span class="icon-play ml-1" />
      </button>
    </div>
  </div>
</template>


<script>
import {
  onBeforeUnmount, onMounted, ref, computed
} from 'vue'
import 'video.js/dist/video-js.css'
import videojs from 'video.js'
import Picture from '@/components/Picture.vue'

export default {
  components: {
    Picture
  },
  props: {
    url: {
      type: String,
      required: true
    },
    mobileUrl: {
      type: String,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    muted: {
      type: Boolean,
      default: true
    },
    controls: {
      type: Boolean,
      default: false
    },
    playsinline: {
      type: Boolean,
      default: true
    },
    heading: {
      type: String,
      default: 'Default'
    },
    cssClass: {
      type: String,
      default: ''
    },
    buttonClass: {
      type: String,
      default: 'bg-blue-light'
    },
    poster: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    let player
    const playerRef = ref()
    const trackingDuration = []
    const availableTrackingDurations = [90, 75, 50, 25, 10]
    const showBtnPlay = ref(!props.autoplay)
    let showPlayBtnOverlay = ref(props.autoplay)
    const videoOptions = computed(() => {
      const {
        autoplay,
        loop,
        muted,
        controls,
        playsinline
      } = props

      const isMobile = window.innerWidth < 768
      const videoSrc = isMobile && props.mobileUrl ? props.mobileUrl : props.url

      return {
        autoplay,
        loop,
        muted,
        controls,
        liveui: controls,
        playsinline,
        sources: [
          {
            src: videoSrc,
            type: 'application/x-mpegURL'
          }
        ]
      }
    })


    const pushDataLayer = (obj) => {
      if (window.dataLayer) {
        window.dataLayer.push(obj)
      }
    }

    const videoStart = (heading) => {
      const gtmObj = {
        event: 'videoStart',
        videoTitle: heading
      }
      pushDataLayer(gtmObj)
    }

    const videoComplete = (heading) => {
      const gtmObj = {
        event: 'videoComplete',
        videoTitle: heading
      }
      pushDataLayer(gtmObj)
    }

    const videoProgress = (heading, duration) => {
      const gtmObj = {
        event: 'videoProgress',
        videoPercent: `${duration}`,
        videoTitle: heading
      }
      pushDataLayer(gtmObj)
    }

    const onHandleClick = () => {
      if (showPlayBtnOverlay) {
        player.play()
        showPlayBtnOverlay = false
        showBtnPlay.value = false
        videoStart(props.heading)
      }
    }

    const getPercent = (tempPercent, percentValue) => {
      if (tempPercent > percentValue && !trackingDuration.includes(percentValue)) {
        return percentValue
      }
      return 0
    }

    onMounted(() => {
      if (!props.autoplay) {
        videoOptions.value.muted = false
        videoOptions.value.controls = true
        videoOptions.value.loop = false
      } else {
        videoOptions.value.loop = true
      }
      player = videojs(playerRef.value, videoOptions.value)
      if (!props.autoplay) {
        player.on('ended', () => {
          videoComplete(props.heading)
        })
        player.on('timeupdate', () => {
          const tempPercent = Math.trunc((player.currentTime() * 100) / player.duration())
          let percent = 0
          for (let i = 0; i < availableTrackingDurations.length; i += 1) {
            percent = getPercent(tempPercent, availableTrackingDurations[i])
            if (percent !== 0) {
              break
            }
          }

          if (percent !== 0 && !trackingDuration.includes(percent)
            && availableTrackingDurations.includes(percent)) {
            videoProgress(props.heading, percent)
            trackingDuration.push(percent)
          }
        })
      }
    })

    onBeforeUnmount(() => {
      if (player) {
        player.dispose()
      }
    })

    return {
      playerRef,
      showPlayBtnOverlay,
      showBtnPlay,
      onHandleClick
    }
  }
}
</script>
<style>
  .video-js .vjs-big-play-button {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .video-js .vjs-tech {
    object-fit: cover;
  }
  .video-js.vjs-has-started + picture {
    display: none;
  }
</style>
